import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { PlayCircle, X, Clapperboard, Maximize2, Download, Mic, FileDown, GripHorizontal } from "lucide-react";
import { useI18n } from "@/i18n";
import { SECTION_VIDEOS, sectionForPath } from "@/videoMap";
import useDragPos from "@/hooks/useDragPos";
import DragWrap from "@/components/DragWrap";

export default function SectionHelp() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const drag = useDragPos("help-player");
  const section = sectionForPath(pathname);
  if (!section || pathname === "/app/guide") return null;
  const vids = SECTION_VIDEOS[section];
  const src = vids ? (vids[lang] || vids.it || Object.values(vids)[0]) : null;
  const title = t(`nav_${section}`) !== `nav_${section}` ? t(`nav_${section}`) : section;

  const goFullscreen = (el) => {
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  return (
    <>
      {!open && (
        <DragWrap dragKey="help-fab" className="fixed bottom-4 right-4 z-[125]">
          <button onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 bg-[#0d1f3d] text-white rounded-full pl-3 pr-4 py-2 text-xs font-semibold shadow-lg hover:opacity-90 transition-opacity"
            data-testid="section-help-btn">
            <PlayCircle className="w-4 h-4 text-[#C5A880]" /> {t("watchHowBtn")}
          </button>
        </DragWrap>
      )}
      {open && (
        <div ref={drag.ref} style={drag.style}
          className="fixed bottom-4 right-4 z-[150] w-[min(94vw,560px)] bg-[#0d1220] rounded-2xl shadow-2xl border border-white/10 overflow-hidden fade-up"
          data-testid="section-help-modal">
          <div onPointerDown={drag.start} style={{ touchAction: "none" }}
            className="flex items-center justify-between pl-3 pr-2 py-2 bg-white/5 cursor-move select-none" data-testid="section-help-drag-handle">
            <div className="text-xs font-semibold flex items-center gap-2 text-white/90 min-w-0">
              <GripHorizontal className="w-4 h-4 text-white/40 shrink-0" />
              <Clapperboard className="w-4 h-4 text-[#C5A880] shrink-0" />
              <span className="truncate">{t("watchHowBtn")} — {title}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {src && (
                <button
                  onClick={(e) => { const v = e.currentTarget.closest("[data-testid='section-help-modal']").querySelector("video"); goFullscreen(v); }}
                  className="p-1.5 rounded-full text-white/70 hover:bg-white/15" title="Schermo intero" data-testid="section-help-fullscreen">
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => setOpen(false)}
                className="p-1.5 rounded-full text-white hover:bg-white/20 transition-colors"
                data-testid="section-help-close" title="Chiudi">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {src ? (
            <video
              src={src}
              controls
              autoPlay
              playsInline
              preload="auto"
              controlsList="nodownload"
              onDoubleClick={(e) => goFullscreen(e.currentTarget)}
              className="w-full aspect-video bg-black"
              data-testid="section-help-video"
            />
          ) : (
            <div className="p-8 text-center bg-white" data-testid="section-help-fallback">
              <Clapperboard className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <div className="font-semibold mb-1">{t("tutorialSoon")}</div>
              <Link to="/app/guide" onClick={() => setOpen(false)} className="text-sm text-primary link-underline" data-testid="section-help-guide-link">{t("tutorialGeneralLink")}</Link>
            </div>
          )}
          {src && (
            <div className="px-3 py-2 flex flex-wrap items-center gap-1.5" data-testid="section-video-dl-hint" title={t("videoDlHint")}>
              <span className="flex items-center gap-1 text-[10px] text-[#C5A880] font-semibold mr-1" data-testid="help-video-voice"><Mic className="w-3 h-3" /> {t("voiceGuideLbl")}</span>
              <a href={lang === "it" ? "/docs/ChefControl-Guida-Completa.pdf" : "/docs/ChefControl-Complete-Guide-EN.pdf"} download
                className="flex items-center gap-1 text-[10px] font-semibold text-[#0d1f3d] bg-[#C5A880] rounded-full px-2.5 py-1.5 hover:opacity-90 transition-opacity" data-testid="help-video-pdf">
                <FileDown className="w-3 h-3" /> {t("pdfGuideBtn")}
              </a>
              {["it", "en", "es", "fr", "de"].map((code) => vids?.[code] && (
                <a key={code} href={vids[code]} download={`chefcontrol_${section}_${code}.mp4`}
                  style={{ backgroundColor: { it: "#0284c7", en: "#0d9488", es: "#ea580c", fr: "#4f46e5", de: "#15803d" }[code] }}
                  className="flex items-center gap-1 text-[10px] font-semibold text-white rounded-full px-2.5 py-1.5 hover:opacity-90 transition-opacity"
                  data-testid={`section-video-dl-${code}`}>
                  <Download className="w-3 h-3" /> {code.toUpperCase()}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
